import {
  faArrowLeft,
  faArrowUpRightFromSquare,
  faClock,
  faImageSlash,
  faLocationDot,
  faMap,
  faPhone,
  faStar,
  faUsers,
  faWheelchair,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { photoUrl } from '@/api/places';
import { useTripContext } from '@/contexts/TripContext';
import { AddToTripButton } from '@/design-system/composite/AddToTripButton';
import { FeaturePill } from '@/design-system/primitives/FeaturePill';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';
import type { ItineraryItem } from '@/types/itinerary';
import { formatAddress } from '@/utils/address';
import {
  categoryDefinitions,
  dedupeCategoriesByLabel,
  humanizeCategoryLabel,
} from '@/utils/categories';
import { formatDayLabel, getTripDayHours } from '@/utils/schedule';
import { tw, twx } from '@/utils/tailwind';
import { formatDuration } from '@/utils/visitDuration';

export interface PlaceDetailPanelProps {
  item: ItineraryItem;
  onClose: () => void;
  variant?: 'overlay' | 'inline';
  /** Label for the inline-variant close button. Defaults to "Back". */
  closeLabel?: string;
  /** Force-render an X close (icon-only) instead of the back-arrow header.
   * Useful when the surrounding context already provides navigation, e.g.
   * Travel mode where there's no list to "go back to". */
  closeAsX?: boolean;
}

const formatPrice = (price?: number): string | null => (price ? '$'.repeat(price) : null);

const trailingZeroDecimal = /\.0$/;

const formatRatingCount = (count?: number): string | null => {
  if (count === undefined) return null;
  if (count >= 1000) return `${(count / 1000).toFixed(1).replace(trailingZeroDecimal, '')}k`;
  return String(count);
};

const formatRating = (rating?: number): string | null =>
  rating !== undefined ? rating.toFixed(1) : null;

const formatTime = (t: string): string => {
  const h = Number(t.slice(0, 2));
  const m = t.slice(2);
  const suffix = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m === '00' ? `${h12}${suffix}` : `${h12}:${m}${suffix}`;
};

const DayLabel: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string | undefined };

const iconStyle = (size: number, vars?: Record<`--fa-${string}`, string>): FAIconStyle =>
  ({ width: size, height: size, ...vars }) as FAIconStyle;

export const PlaceDetailPanel = ({
  item,
  onClose,
  variant = 'overlay',
  closeLabel = 'Back',
  closeAsX = false,
}: PlaceDetailPanelProps) => {
  const { trips, selectedTripId } = useTripContext();
  const isInline = variant === 'inline';
  const def = categoryDefinitions[item.appCategory];
  // User-created custom pins are sparse — they have a name + coords +
  // category but none of the provider-side enrichments (photos, hours,
  // rating, website, …). We hide every provider-only UI block when this
  // is true. Apple Maps link still renders since it's built from name +
  // lat/lng, which user pins always have.
  const isUserPin = item.source === 'user';
  const heroPhoto = isUserPin ? undefined : item.photos[0];

  // Track when the hero photo fails to load so we can swap in the placeholder
  // even when item.photos[0] exists. Reset when navigating to a different
  // place — otherwise switching cards would keep the failure state.
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  useEffect(() => {
    // item.id is the reset trigger — referenced via void so the linter keeps it in deps
    void item.id;
    setImageFailed(false);
    setImageLoaded(false);
  }, [item.id]);
  const price = formatPrice(item.price);
  const rating = formatRating(item.rating);
  const ratingCount = formatRatingCount(item.userRatingCount);
  const duration = formatDuration(item.estimatedDuration);
  const selectedTrip = trips.find((t) => t.id === selectedTripId) ?? null;
  const tripDayHours =
    selectedTrip && item.hours?.regular && item.hours.regular.length > 0
      ? getTripDayHours(
          { startDate: selectedTrip.startDate, endDate: selectedTrip.endDate },
          item.hours.regular,
        )
      : null;

  const today = new Date().getDay() || 7;
  const isClosed =
    item.businessStatus === 'PERMANENTLY_CLOSED' || item.businessStatus === 'TEMPORARILY_CLOSED';
  const accessibility = item.accessibilityOptions;
  const accessibilityFeatures = accessibility
    ? (
        [
          accessibility.wheelchairAccessibleEntrance && 'Entrance',
          accessibility.wheelchairAccessibleParking && 'Parking',
          accessibility.wheelchairAccessibleRestroom && 'Restroom',
          accessibility.wheelchairAccessibleSeating && 'Seating',
        ] as Array<string | false>
      ).filter((f): f is string => f !== false)
    : [];

  const appleMapsUrl = `https://maps.apple.com/?q=${encodeURIComponent(item.name)}&ll=${item.latitude},${item.longitude}`;

  // Service options badges
  const serviceOptionEntries: Array<[keyof ItineraryItem, string]> = [
    ['takeout', 'Takeout'],
    ['delivery', 'Delivery'],
    ['dineIn', 'Dine-in'],
    ['reservable', 'Reservable'],
    ['curbsidePickup', 'Curbside pickup'],
    ['outdoorSeating', 'Outdoor seating'],
  ];
  const activeServiceOptions = serviceOptionEntries.filter(([key]) => item[key] === true);

  // Atmosphere badges
  const atmosphereEntries: Array<[keyof ItineraryItem, string]> = [
    ['goodForGroups', 'Good for groups'],
    ['goodForChildren', 'Good for kids'],
    ['allowsDogs', 'Dog-friendly'],
    ['liveMusic', 'Live music'],
  ];
  const activeAtmosphere = atmosphereEntries.filter(([key]) => item[key] === true);

  // Dining badges
  const diningEntries: Array<[keyof ItineraryItem, string]> = [
    ['servesBreakfast', 'Breakfast'],
    ['servesBrunch', 'Brunch'],
    ['servesLunch', 'Lunch'],
    ['servesDinner', 'Dinner'],
    ['servesBeer', 'Beer'],
    ['servesWine', 'Wine'],
    ['servesCocktails', 'Cocktails'],
    ['servesCoffee', 'Coffee'],
    ['servesVegetarianFood', 'Vegetarian'],
    ['servesDessert', 'Dessert'],
  ];
  const activeDining =
    item.appCategory === 'dining' ? diningEntries.filter(([key]) => item[key] === true) : [];

  // Parking badges
  type ParkingKey =
    | 'freeParkingLot'
    | 'paidParkingLot'
    | 'freeStreetParking'
    | 'paidStreetParking'
    | 'valetParking'
    | 'freeGarageParking'
    | 'paidGarageParking';
  const parkingEntries: Array<[ParkingKey, string]> = [
    ['freeParkingLot', 'Free lot'],
    ['paidParkingLot', 'Paid lot'],
    ['freeStreetParking', 'Free street'],
    ['paidStreetParking', 'Paid street'],
    ['valetParking', 'Valet'],
    ['freeGarageParking', 'Free garage'],
    ['paidGarageParking', 'Paid garage'],
  ];
  const activeParking = item.parkingOptions
    ? parkingEntries.filter(([key]) => item.parkingOptions?.[key] === true)
    : [];

  // Payment badges
  type PaymentKey = 'acceptsCreditCards' | 'acceptsDebitCards' | 'acceptsCashOnly' | 'acceptsNfc';
  const paymentEntries: Array<[PaymentKey, string]> = [
    ['acceptsCreditCards', 'Credit cards'],
    ['acceptsDebitCards', 'Debit cards'],
    ['acceptsCashOnly', 'Cash only'],
    ['acceptsNfc', 'Contactless'],
  ];
  const activePayment = item.paymentOptions
    ? paymentEntries.filter(([key]) => item.paymentOptions?.[key] === true)
    : [];

  const panel = (
    <div
      className={
        isInline
          ? 'w-full flex flex-col overflow-hidden bg-white h-full'
          : 'fixed inset-y-0 right-0 w-full max-w-[480px] bg-white shadow-2xl z-modal flex flex-col overflow-hidden'
      }
    >
      {isInline ? (
        /* Inline header — either a back-arrow + label, or just an X. */
        <div
          className={twx(
            'shrink-0 flex items-center gap-2 px-3 py-2 border-b border-content-divider',
            closeAsX ? 'justify-end' : '',
          )}
        >
          <button
            type="button"
            onClick={onClose}
            className={
              closeAsX
                ? 'w-8 h-8 rounded-full hover:bg-content-focal flex items-center justify-center text-content-subtle hover:text-content-text transition-colors'
                : 'flex items-center gap-1.5 text-sm text-content-subtle hover:text-content-text transition-colors font-medium'
            }
            aria-label={closeAsX ? 'Close' : closeLabel}
          >
            <FontAwesomeIcon
              icon={closeAsX ? faXmark : faArrowLeft}
              style={iconStyle(closeAsX ? 14 : 13)}
            />
            {!closeAsX && closeLabel}
          </button>
        </div>
      ) : (
        /* Overlay close button */
        <button
          type="button"
          onClick={onClose}
          className={tw`
            absolute top-3 right-3 z-10
            w-8 h-8 flex items-center justify-center
            bg-white/90 backdrop-blur-sm rounded-full shadow-sm border border-content-border
            text-content-subtle hover:text-content-text transition-colors
          `}
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faXmark} style={iconStyle(16)} />
        </button>
      )}

      {/* Scroll area. The hero photo lives inside this so it scrolls
          away with content (otherwise mobile users can't pan-drag from
          the photo, and the photo eats most of the bottom-sheet height).
          The condensed name/category/distance header below the photo is
          `sticky top-0` so the user always knows what they're looking
          at as they scroll through details, hours, etc. */}
      <div className="flex-1 overflow-y-auto">
        {/* Hero photo + action buttons. Mobile clamps to h-28 banners
            for both the loaded and "no photo" states so the bottom sheet
            doesn't get swallowed by a placeholder. Desktop keeps the
            cinematic 16:9 hero capped at 32vh. */}
        <div className="relative w-full">
          {heroPhoto && !imageFailed ? (
            <div className="relative w-full h-28 md:h-auto md:aspect-video md:max-h-[32vh] overflow-hidden bg-content-focal">
              {!imageLoaded && (
                <div
                  aria-hidden
                  className="absolute inset-0 bg-kawaii-shimmer motion-safe:animate-kawaii-shimmer"
                />
              )}
              <img
                src={photoUrl(heroPhoto, 800)}
                alt={item.name}
                className="relative w-full h-full object-cover transition-opacity duration-300"
                style={{ opacity: imageLoaded ? 1 : 0 }}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageFailed(true)}
              />
            </div>
          ) : isUserPin ? (
            // Compact backdrop for custom pins — they have no photo by
            // definition, so a tall "No photo available" placeholder
            // would just be a wasted scroll. Keep enough height for the
            // action button strip to overlap cleanly.
            <div className="w-full h-16 bg-gradient-to-br from-secondary-subtle via-tertiary-subtle to-primary-subtle" />
          ) : (
            <div className="w-full h-28 md:h-auto md:aspect-video md:max-h-[32vh] flex flex-col items-center justify-center gap-1.5 bg-gradient-to-br from-content-focal to-content-background text-content-disabled">
              <FontAwesomeIcon icon={faImageSlash} style={{ width: 24, height: 24 }} />
              <span className="text-xs font-medium">
                {heroPhoto ? "Couldn't load photo" : 'No photo available'}
              </span>
            </div>
          )}
          {/* Gradient + action icon buttons overlapping the photo bottom */}
          <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
          <div className="absolute bottom-2 right-2 flex gap-1.5">
            <AddToTripButton item={item} variant="overlay" />
            {item.website && (
              <a
                href={item.website}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md border border-white/30 text-content-text hover:bg-white transition-[colors,transform] motion-safe:active:scale-90"
                aria-label="Visit website"
                title="Visit website"
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={iconStyle(13)} />
              </a>
            )}
            <a
              href={appleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md border border-white/30 text-content-text hover:bg-white transition-[colors,transform] motion-safe:active:scale-90"
              aria-label="Open in Apple Maps"
              title="Open in Apple Maps"
            >
              <FontAwesomeIcon
                icon={faLocationDot}
                style={iconStyle(13, { '--fa-primary-color': 'var(--color-primary-emphasized)' })}
              />
            </a>
            {item.googleMapsUri && (
              <a
                href={item.googleMapsUri}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-md border border-white/30 text-content-text hover:bg-white transition-[colors,transform] motion-safe:active:scale-90"
                aria-label="Open in Google Maps"
                title="Open in Google Maps"
              >
                <FontAwesomeIcon
                  icon={faMap}
                  style={iconStyle(13, {
                    '--fa-primary-color': 'var(--color-secondary-emphasized)',
                  })}
                />
              </a>
            )}
          </div>
        </div>

        {/* Condensed sticky header — categories, title, meta. Keeps the
            user oriented as the rest of the panel scrolls underneath. */}
        <div className="sticky top-0 z-10 bg-white border-b border-content-divider px-4 py-2.5 space-y-1.5">
          <div className="flex flex-wrap gap-1.5">
            {isUserPin && (
              <span
                className={tw`
                    inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border
                    ${def.badgeClasses.bg} ${def.badgeClasses.text} ${def.badgeClasses.border}
                  `}
                title="A custom pin you added"
              >
                <FontAwesomeIcon icon={def.icon} style={{ width: 10, height: 10 }} />
                Custom pin · {def.label}
              </span>
            )}
            {/* Uncapped. The row previously showed at most three, which hid
                real categories on the ~300 places carrying more — and the
                duplicates it was implicitly compensating for are now collapsed
                by deduping instead. The container wraps, and the observed
                ceiling is 9 distinct labels, so this stays bounded. */}
            {dedupeCategoriesByLabel(item.categories).map((cat) => (
              <span
                key={cat.id}
                className={tw`
                    inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border
                    ${def.badgeClasses.bg} ${def.badgeClasses.text} ${def.badgeClasses.border}
                  `}
              >
                <FontAwesomeIcon icon={def.icon} style={{ width: 10, height: 10 }} />
                {humanizeCategoryLabel(cat.name)}
              </span>
            ))}
            {isClosed && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border bg-error-subtle text-error-main border-error-main/20">
                {item.businessStatus === 'PERMANENTLY_CLOSED'
                  ? 'Permanently closed'
                  : 'Temporarily closed'}
              </span>
            )}
          </div>
          <h2 className="font-heading font-semibold text-xl text-content-text leading-tight">
            {item.name}
          </h2>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-content-subtle">
            {rating && (
              <span className="flex items-center gap-1">
                <FontAwesomeIcon
                  icon={faStar}
                  style={iconStyle(12, {
                    '--fa-primary-color': 'var(--color-tertiary-main)',
                    '--fa-secondary-color': 'var(--color-tertiary-main)',
                  })}
                />
                <span className="font-mono font-medium text-content-text">{rating}</span>
                {ratingCount && <span className="text-content-disabled">({ratingCount})</span>}
              </span>
            )}
            {(price ?? item.priceRange) && (
              <span className="text-content-deemphasized font-medium">
                {item.priceRange ?? price}
              </span>
            )}
            {item.distanceMiles !== undefined && (
              <span>{item.distanceMiles.toFixed(1)} mi away</span>
            )}
          </div>
        </div>

        <div className="p-3 md:p-4 space-y-3 md:space-y-4">
          {item.editorialSummary && (
            <p className="text-xs md:text-sm text-content-subtle leading-relaxed">
              {item.editorialSummary}
            </p>
          )}

          {/* Details */}
          <section className="space-y-1.5">
            <SectionEyebrow as="h3">Details</SectionEyebrow>
            <div className="space-y-1 md:space-y-1.5 text-xs md:text-sm">
              <div className="flex items-center gap-2 text-content-text">
                <FontAwesomeIcon
                  icon={faClock}
                  style={iconStyle(12)}
                  className="text-content-subtle shrink-0"
                />
                <span>
                  <span className="text-content-subtle mr-1">Visit:</span>
                  {duration}
                </span>
              </div>
              <div className="flex items-center gap-2 text-content-text">
                <FontAwesomeIcon
                  icon={faUsers}
                  style={iconStyle(12)}
                  className="text-content-subtle shrink-0"
                />
                <span>
                  <span className="text-content-subtle mr-1">Good for:</span>
                  {item.suggestedGroupSize}
                </span>
              </div>
              {item.address ? (
                <div className="flex items-start gap-2 text-content-text">
                  <FontAwesomeIcon
                    icon={faLocationDot}
                    style={iconStyle(12)}
                    className="text-content-subtle shrink-0 mt-0.5"
                  />
                  <span className="leading-snug">{formatAddress(item.address)}</span>
                </div>
              ) : null}
              {item.phone && (
                <div className="flex items-center gap-2 text-content-text">
                  <FontAwesomeIcon
                    icon={faPhone}
                    style={iconStyle(12)}
                    className="text-content-subtle shrink-0"
                  />
                  <a
                    href={`tel:${item.phone}`}
                    className="text-secondary-emphasized hover:underline font-mono"
                  >
                    {item.phone}
                  </a>
                </div>
              )}
            </div>
          </section>

          {/* Photos live on the timeline rows (TripPlaceRow), not in
              the place-detail panel — see PlacePhotosCorner inside
              TripPlaceRow. Decided so users can attach + flip through
              photos while curating a day without having to drill into a
              detail panel for each place. */}

          {/* Hours */}
          {item.hours && (
            <section className="space-y-2">
              <div className="flex justify-between items-center">
                <SectionEyebrow as="h3">Hours</SectionEyebrow>
                <span
                  className={twx(
                    'inline-flex items-center gap-1.5 text-xs font-medium',
                    item.hours.isOpenNow ? 'text-success-main' : 'text-error-main',
                  )}
                >
                  <span
                    className={twx(
                      'w-1.5 h-1.5 rounded-full',
                      item.hours.isOpenNow ? 'bg-success-main' : 'bg-error-main',
                    )}
                  />
                  {item.hours.isOpenNow ? 'Open now' : 'Closed'}
                </span>
              </div>

              {/* Trip day hours — shown when a trip is selected */}
              {tripDayHours && (
                <div className="space-y-0.5 mb-2">
                  <p className="text-xs font-semibold text-primary-emphasized uppercase tracking-wider">
                    During your trip
                  </p>
                  <div className="space-y-0.5">
                    {tripDayHours.map(({ isoDate, hours }) => (
                      <div
                        key={isoDate}
                        className="flex items-baseline text-sm py-1 px-3 rounded-lg bg-primary-subtle/40"
                      >
                        <span className="text-content-subtle shrink-0">
                          {formatDayLabel(isoDate)}
                        </span>
                        <span className="flex-1 border-b border-dotted border-content-disabled/40 mx-2 relative top-[-2px]" />
                        {hours ? (
                          <span className="font-mono tabular-nums text-content-text shrink-0">
                            {formatTime(hours.open)} – {formatTime(hours.close)}
                          </span>
                        ) : (
                          <span className="text-error-main font-medium shrink-0">Closed</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {item.hours.regular && item.hours.regular.length > 0 ? (
                <div className="space-y-0.5">
                  <p className="text-sm font-semibold text-content-subtle uppercase tracking-wider mb-1">
                    Regular hours
                  </p>
                  {item.hours.regular.map((h) => (
                    <div
                      key={h.day}
                      className={twx(
                        'flex items-baseline text-sm py-1 px-3 rounded-lg',
                        h.day === today ? 'bg-secondary-main/8 font-medium' : '',
                      )}
                    >
                      <span
                        className={twx(
                          'shrink-0',
                          h.day === today ? 'text-secondary-emphasized' : 'text-content-subtle',
                        )}
                      >
                        {DayLabel[h.day]}
                      </span>
                      <span className="flex-1 border-b border-dotted border-content-disabled/40 mx-2 relative top-[-2px]" />
                      <span className="font-mono tabular-nums text-content-text shrink-0">
                        {formatTime(h.open)} – {formatTime(h.close)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                item.hours.display && (
                  <p className="text-sm text-content-text">{item.hours.display}</p>
                )
              )}
            </section>
          )}

          {/* Accessibility */}
          {accessibilityFeatures.length > 0 && (
            <section className="space-y-2">
              <SectionEyebrow as="h3">Accessibility</SectionEyebrow>
              <div className="flex flex-wrap gap-2">
                {accessibilityFeatures.map((feature) => (
                  <span
                    key={feature}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-content-background text-content-text border border-content-border"
                  >
                    <FontAwesomeIcon
                      icon={faWheelchair}
                      style={iconStyle(11)}
                      className="text-content-subtle"
                    />
                    {feature}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Service options */}
          {activeServiceOptions.length > 0 && (
            <section className="space-y-2">
              <SectionEyebrow as="h3">Service options</SectionEyebrow>
              <div className="flex flex-wrap gap-2">
                {activeServiceOptions.map(([key, label]) => (
                  <FeaturePill key={key} tone="secondary">
                    {label}
                  </FeaturePill>
                ))}
              </div>
            </section>
          )}

          {/* Atmosphere */}
          {activeAtmosphere.length > 0 && (
            <section className="space-y-2">
              <SectionEyebrow as="h3">Atmosphere</SectionEyebrow>
              <div className="flex flex-wrap gap-2">
                {activeAtmosphere.map(([key, label]) => (
                  <FeaturePill key={key} tone="tertiary">
                    {label}
                  </FeaturePill>
                ))}
              </div>
            </section>
          )}

          {/* Dining */}
          {activeDining.length > 0 && (
            <section className="space-y-2">
              <SectionEyebrow as="h3">Dining</SectionEyebrow>
              <div className="flex flex-wrap gap-2">
                {activeDining.map(([key, label]) => (
                  <FeaturePill key={key} tone="tertiary">
                    {label}
                  </FeaturePill>
                ))}
              </div>
            </section>
          )}

          {/* Parking */}
          {activeParking.length > 0 && (
            <section className="space-y-2">
              <SectionEyebrow as="h3">Parking</SectionEyebrow>
              <div className="flex flex-wrap gap-2">
                {activeParking.map(([key, label]) => (
                  <FeaturePill key={key} tone="quaternary">
                    {label}
                  </FeaturePill>
                ))}
              </div>
            </section>
          )}

          {/* Payment */}
          {activePayment.length > 0 && (
            <section className="space-y-2">
              <SectionEyebrow as="h3">Payment</SectionEyebrow>
              <div className="flex flex-wrap gap-2">
                {activePayment.map(([key, label]) => (
                  <span
                    key={key}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-content-background text-content-text border border-content-border"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );

  if (isInline) return panel;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 z-modal-scrim"
        onClick={onClose}
        aria-hidden={true}
      />
      {panel}
    </>
  );
};
